'use client';

import config from '@actr/config';
import { loadDIModules, useInject } from '@actr/di';
import { useNavigation } from '@actr/hooks/src/navigation/hook';
import hotelFromAviaDIModule from '@actr/stores/src/modules/avia/hotel-from-avia';
import hotelSearchDIModule from '@actr/stores/src/modules/hotel/search';
import { HotelFromAviaService } from '@actr/stores/src/services/services/avia/hotel-from-avia';
import { ThemeColorValueTypes } from '@actr/style/src/specs/theme-v2';
import { Text } from '@actr/ui/src/components/_atoms/text';
import { TextDisplay } from '@actr/ui/src/components/_atoms/text/specs';
import { Container } from '@actr/ui/src/components/container';
import Navigation from '@actr/widgets/src/components/_common/navigation';
import AviaSearchFormRedirect from '@actr/widgets/src/components/avia/search/search-form/redirect';
import { usePrefillSearchFromUrl } from '@actr/widgets/src/hooks/avia/use-prefill-search-from-url';
import aviaSearchLogger, { AviaSearchLogs } from '@actr/widgets/src/logs/avia/search';
import aviaSearchYMLogger, { AviaSearchYMLogs } from '@actr/widgets/src/logs/ym/avia/search';
import { AkV2Routes } from '@actr/widgets/src/navigation/routes/ak-v2';
import { observer } from 'mobx-react-lite';
import { searchParamsToUrlQuery } from 'next/dist/shared/lib/router/utils/querystring';
import { usePathname } from 'next/navigation';
import React from 'react';

import * as Styled from './style.css';

export type SearchFormHeaderProps = {
  title: string;
  withNavigation?: boolean;
  wrapTitleInSpan?: boolean;
}

const SearchFormHeader: React.FC<SearchFormHeaderProps> = observer(({ title, withNavigation = false, wrapTitleInSpan = false }) => {
  const pathname = usePathname();
  const { navigate, createRouteUrl } = useNavigation();
  const hotelFromAviaService = useInject(HotelFromAviaService);
  usePrefillSearchFromUrl();

  const handleFinish = (sro: string) => {
    const searchParams = searchParamsToUrlQuery(new URLSearchParams(sro)) as Record<string, string | undefined>;
    if (hotelFromAviaService.isEnabled) {
      window.open(createRouteUrl(AkV2Routes.AVIA_SEARCH, {
        searchParams,
        short: false,
      }), '_blank');
      hotelFromAviaService.getHotelDestinationFromAvia()
        .then(destination => {
          if (destination) {
            const hotelSearchId = hotelFromAviaService.getSearchIdForHotel();
            aviaSearchLogger.send(AviaSearchLogs.OPEN_HOTELS_TAB_SUCCESS);
            aviaSearchYMLogger.send(AviaSearchYMLogs.OPEN_HOTELS_TAB_SUCCESS);
            navigate(AkV2Routes.HOTEL_RESULTS, {
              origin: config.aviakassaURL,
              routeParams: {
                searchId: hotelSearchId,
              },
              searchParams: {
                age_correction_modal: '1',
              },
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-expect-error
              short: false,
              useRouter: false
            });
          }
        });
    } else {
      setTimeout(() => {
        navigate(AkV2Routes.AVIA_SEARCH, {
          searchParams,
          useRouter: false
        });
      }, 100);
    }
  };

  return (
    <div className={Styled.Wrapper}>
      <Container>
        <Text
          className={Styled.Title}
          tagName={wrapTitleInSpan ? 'span' : 'h1'}
          font="title.x4l"
          display={TextDisplay.BLOCK}
          colorType={ThemeColorValueTypes.INVERTED}>
          {title}
        </Text>
        {withNavigation && <Navigation externalPathname={pathname} />}
        <AviaSearchFormRedirect
          autoFocusAfterFill={true}
          onFinish={handleFinish}
        />
      </Container>
    </div>
  );
});

export default loadDIModules(SearchFormHeader, hotelSearchDIModule, hotelFromAviaDIModule);
